import { useRef, useMemo } from 'react';
import {
    Box,
    HStack,
    InputGroup,
    InputRightElement,
    Stack,
    Switch,
} from '@chakra-ui/react';
import { TbTrash } from 'react-icons/tb';

import { BUTTON_WIDTH } from 'pages/settingsSidebar/security/sso/constants';

import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import { Button, Icon, Input, Text } from 'components/v4';

/**
 * Shared presentational form body used by both AddProvider and EditMicrosoftSSO.
 * Receives all state and handlers from useTenantDomainForm via props.
 */
export default function TenantDomainFormBody({
    // Lookup input
    inputValue,
    inputStatus,
    inputError,
    handleInputChange,
    handleAddAction,
    // Tenants
    tenantIds,
    removeTenant,
    // Domain restriction toggle
    showDomainRestriction,
    setShowDomainRestriction,
    // Domain input
    domainInputValue,
    domainInputError,
    handleDomainInputChange,
    handleAddDomain,
    // Domains
    domains,
    removeDomain,
    // Config
    darkMode,
    titlePrefix = 'sso',
    columnFlex = 1,
    emptyGridHeight = '100px',
}) {
    const tenantGridRef = useRef(null);
    const domainGridRef = useRef(null);

    const tenantColumnDefs = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'TenantID',
                flex: columnFlex,
                sortable: false,
                filter: false,
                cellStyle: { display: 'flex', alignItems: 'center' },
                cellRenderer: (params) => (
                    <Box fontFamily="mono" fontSize="xs" color="messenger.500">
                        {params.value}
                    </Box>
                ),
            },
            {
                headerName: 'Action',
                headerClass: 'ag-right-aligned-header',
                width: 80,
                sortable: false,
                filter: false,
                suppressMovable: true,
                lockPosition: 'right',
                resizable: false,
                suppressHeaderMenuButton: true,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                },
                cellRenderer: (params) => (
                    <Icon
                        as={TbTrash}
                        cursor="pointer"
                        boxSize={4}
                        color="gray.400"
                        _hover={{ color: 'red.500' }}
                        onClick={() => removeTenant(params.data.id)}
                    />
                ),
            },
        ],
        [removeTenant, columnFlex],
    );

    const domainColumnDefs = useMemo(
        () => [
            {
                field: 'domain',
                headerName: 'Domain',
                flex: columnFlex,
                sortable: false,
                filter: false,
                cellStyle: { display: 'flex', alignItems: 'center' },
            },
            {
                headerName: 'Action',
                headerClass: 'ag-right-aligned-header',
                width: 80,
                sortable: false,
                filter: false,
                suppressMovable: true,
                lockPosition: 'right',
                resizable: false,
                suppressHeaderMenuButton: true,
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                },
                cellRenderer: (params) => (
                    <Icon
                        as={TbTrash}
                        cursor="pointer"
                        boxSize={4}
                        color="gray.400"
                        _hover={{ color: 'red.500' }}
                        onClick={() => removeDomain(params.data.domain)}
                    />
                ),
            },
        ],
        [removeDomain, columnFlex],
    );

    return (
        <Stack
            spacing={6}
            borderTopWidth={1}
            borderColor="gray.100"
            pt={6}
            mt={4}>
            {/* Lookup input */}
            <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Lookup TenantID or domain name
                </Text>
                <InputGroup>
                    <Input
                        value={inputValue}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder="e.g. acme.com or GUID"
                        isInvalid={inputStatus === 'error'}
                        onKeyDown={(e) =>
                            e.key === 'Enter' && handleAddAction()
                        }
                        pr={BUTTON_WIDTH}
                    />
                    <InputRightElement width={BUTTON_WIDTH}>
                        <Button
                            onClick={handleAddAction}
                            isLoading={inputStatus === 'loading'}
                            h="2.5rem"
                            w="full"
                            borderStartRadius={0}>
                            Add
                        </Button>
                    </InputRightElement>
                </InputGroup>
                {inputStatus === 'error' && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                        {inputError}
                    </Text>
                )}
            </Box>

            {/* Allowed Tenants grid */}
            <Box>
                <Text
                    fontSize="xs"
                    fontWeight="bold"
                    letterSpacing="wider"
                    mb={3}>
                    Allowed Tenants
                </Text>
                <AGGridMain
                    noSearchBar
                    suppressPaginationPanel
                    tableData={{
                        columns: tenantColumnDefs,
                        data: tenantIds.map((id) => ({ id })),
                    }}
                    gridRef={tenantGridRef}
                    useFixedHeight={false}
                    domLayout={tenantIds.length > 0 ? 'autoHeight' : 'normal'}
                    customHeight={
                        tenantIds.length === 0 ? emptyGridHeight : 'auto'
                    }
                    overlayNoRowsTemplate="<span>No tenants added yet</span>"
                    titleName={`${titlePrefix}-tenants`}
                />
            </Box>

            {/* Domain restriction toggle */}
            <HStack
                bg={darkMode ? 'gray.700' : 'gray.50'}
                borderRadius="lg"
                p={4}
                justify="space-between"
                align="center"
                borderWidth={1}
                borderColor="gray.100"
                spacing={4}>
                <Text fontSize="xs" color="gray.500">
                    Enable if you wish to only allow certain domains from a
                    tenant. Both TenantID should be added above and the domains
                    below.
                </Text>
                <Switch
                    isChecked={showDomainRestriction}
                    onChange={() => setShowDomainRestriction((v) => !v)}
                    flexShrink={0}
                />
            </HStack>

            {/* Allowed Domains section */}
            {showDomainRestriction && (
                <Stack spacing={3}>
                    <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="wider">
                        Allowed Domains
                    </Text>
                    <InputGroup>
                        <Input
                            value={domainInputValue}
                            onChange={(e) =>
                                handleDomainInputChange(e.target.value)
                            }
                            placeholder="e.g. acme.com"
                            isInvalid={Boolean(domainInputError)}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleAddDomain()
                            }
                            pr={BUTTON_WIDTH}
                        />
                        <InputRightElement width={BUTTON_WIDTH}>
                            <Button
                                onClick={handleAddDomain}
                                variant="outline"
                                bg="white"
                                h="2.5rem"
                                w="full"
                                borderStartRadius={0}>
                                Add
                            </Button>
                        </InputRightElement>
                    </InputGroup>
                    {domainInputError && (
                        <Text fontSize="xs" color="red.500">
                            {domainInputError}
                        </Text>
                    )}
                    <AGGridMain
                        noSearchBar
                        suppressPaginationPanel
                        tableData={{
                            columns: domainColumnDefs,
                            data: domains.map((domain) => ({ domain })),
                        }}
                        gridRef={domainGridRef}
                        useFixedHeight={false}
                        domLayout={domains.length > 0 ? 'autoHeight' : 'normal'}
                        customHeight={
                            domains.length === 0 ? emptyGridHeight : 'auto'
                        }
                        overlayNoRowsTemplate="<span>No domains added yet</span>"
                        titleName={`${titlePrefix}-domains`}
                    />
                </Stack>
            )}
        </Stack>
    );
}
